import { useAppTFunction } from '@retail/app/i18n';
import {
  ProductGroup as BaseProductGroup,
  DiscountGroup,
  GroupOption,
} from '@retail/products/types';
import {
  ErrorFallback,
  GroupOptionToggle,
  ProductGroupMultiselectFilter,
  SideFilters,
  ToggleGroup,
  ToggleSwitch,
} from '@retail/components';
import { MgSupplierItemSearchField, SelectDiscountGroups } from '@retail/products/components';
import { HierarchyFilterContext, FilterToggle } from '@retail/my-assortment/types';
import { Skeleton } from '@mui/material';
import { itemPricesFilters, ItemPricesFilters } from '@retail/my-assortment/utils';
import { useFilters } from '@retail/products/hooks';
import { useEffect } from 'react';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';

interface Props {
  showFilters: boolean;
  filterOptions?: HierarchyFilterContext;
  errorFetchingFilters: boolean;
  loadingFilters: boolean;
  productGroupNumbers?: string[];
  discountGroupCodes: string[];
  searchQuery: string | null;
  inAssortment: GroupOption;
  purchasePrice?: GroupOption;
  includeVat?: FilterToggle;
  includeAssortmentFilter: boolean;
  forApproval: boolean;
  disableNotInAssortment: boolean;
}

export function ItemPricesFilters({
  showFilters,
  filterOptions,
  errorFetchingFilters,
  loadingFilters,
  productGroupNumbers,
  discountGroupCodes,
  searchQuery,
  inAssortment,
  purchasePrice,
  includeVat,
  includeAssortmentFilter,
  forApproval,
  disableNotInAssortment,
}: Props) {
  const t = useAppTFunction();
  const tMyAssortment = useMyAssortmentTFunction();
  const { setFilters, updateFilter } = useFilters<ItemPricesFilters>(itemPricesFilters);

  useEffect(() => {
    if (disableNotInAssortment && inAssortment !== 'UNSPECIFIED') {
      setFilters({ inAssortment: undefined });
    }
  }, [disableNotInAssortment, inAssortment, setFilters]);

  return (
    <SideFilters showFilters={showFilters}>
      {includeVat && (
        <ToggleSwitch
          text={t('myAssortment.actions.includeVat')}
          justifyContent="space-between"
          toggle={includeVat.toggle}
          setToggle={includeVat.onToggle}
        />
      )}

      {includeAssortmentFilter && (
        <>
          <ToggleSwitch
            text={t('myAssortment.filters.onlyForApprovalItems')}
            justifyContent="space-between"
            toggle={forApproval}
            setToggle={() => setFilters({ forApproval: forApproval ? undefined : true })}
          />
          <ToggleGroup
            variant="pill"
            size="small"
            label={t('myAssortment.status.inAssortment')}
            labelDirection="vertical"
            disabled={disableNotInAssortment}
            options={[
              { label: t('myAssortment.status.inAssortment'), value: 'UNSPECIFIED' },
              { label: t('myAssortment.status.notInAssortment'), value: 'NO' },
            ]}
            value={inAssortment === 'UNSPECIFIED' ? 'UNSPECIFIED' : 'NO'}
            onChange={(option: GroupOption) =>
              setFilters({ inAssortment: option === 'UNSPECIFIED' ? undefined : option })
            }
          />
        </>
      )}

      <GroupOptionToggle
        label={t('myAssortment.columns.purchasePrice.price')}
        value={purchasePrice ?? 'UNSPECIFIED'}
        allLabel={tMyAssortment('myAssortment.filter.all')}
        yesLabel={tMyAssortment('myAssortment.filter.withPurchasePrice')}
        noLabel={tMyAssortment('myAssortment.filter.withoutPurchasePrice')}
        onChange={(option: GroupOption) =>
          setFilters({ purchasePrice: option === 'UNSPECIFIED' ? undefined : option })
        }
      />
      {errorFetchingFilters ? (
        <ErrorFallback message={t('common.genericError')} />
      ) : loadingFilters ? (
        <Skeleton />
      ) : (
        <>
          <MgSupplierItemSearchField
            isDisabled={false}
            size="small"
            searchTerm={searchQuery ?? ''}
            onSubmitSearch={(query: string) => setFilters({ searchQuery: query })}
          />
          <SelectDiscountGroups
            discountGroups={filterOptions?.discountGroupsForSupplier ?? []}
            selectedDiscountGroups={discountGroupCodes}
            getDiscountGroupKey={({ code }: DiscountGroup) => code}
            selectDiscountGroups={(groupCodes: string[]) =>
              setFilters({ discountGroupCodes: groupCodes })
            }
          />
          <ProductGroupMultiselectFilter
            productGroups={filterOptions?.productGroups ?? []}
            selectedProductGroups={productGroupNumbers ?? []}
            selectProductGroup={(groupNumber: string) =>
              updateFilter('productGroupNumbers', groupNumber)
            }
            getCount={(productGroup: BaseProductGroup) => productGroup.count}
          />
        </>
      )}
    </SideFilters>
  );
}
